import type { DecorationToggles } from '@/types/announcement';

interface DecorationOverlaysProps {
  decorations: DecorationToggles;
  fontColor?: string;
}

export function DecorationOverlays({ decorations, fontColor = '#1a1a2e' }: DecorationOverlaysProps) {
  const isLight = fontColor === '#ffffff' || fontColor === '#f5f5f5' || fontColor === '#e2e8f0';
  const accent = isLight ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)';
  const accentStrong = isLight ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.1)';

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden z-[5]">
      {/* Hearts - corner accents */}
      {decorations.hearts && (
        <>
          <span className="absolute top-4 right-4 text-2xl opacity-40 animate-pulse">♥</span>
          <span className="absolute bottom-8 left-6 text-lg opacity-30 -rotate-12">♥</span>
          <span className="absolute top-1/3 right-8 text-sm opacity-20">♥</span>
        </>
      )}

      {/* Sparkles */}
      {decorations.sparkles && (
        <>
          <span className="absolute top-6 left-8 text-xl opacity-40 animate-pulse">✦</span>
          <span className="absolute top-12 right-12 text-sm opacity-30" style={{ animationDelay: '0.5s' }}>✧</span>
          <span className="absolute bottom-16 right-6 text-lg opacity-25 animate-pulse" style={{ animationDelay: '1s' }}>✦</span>
          <span className="absolute bottom-1/3 left-4 text-xs opacity-20">✧</span>
        </>
      )}

      {/* Lines / dividers */}
      {decorations.lines && (
        <>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px" style={{ backgroundColor: accent }} />
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-24 h-px" style={{ backgroundColor: accent }} />
          <div className="absolute top-0 left-6 w-px h-12" style={{ backgroundColor: accent }} />
          <div className="absolute top-0 right-6 w-px h-12" style={{ backgroundColor: accent }} />
        </>
      )}

      {/* Tape / scrapbook accents */}
      {decorations.tape && (
        <>
          <div className="absolute top-3 left-4 w-16 h-5 bg-amber-200/40 rotate-[-5deg] rounded-sm" />
          <div className="absolute bottom-6 right-4 w-14 h-5 bg-amber-200/30 rotate-[3deg] rounded-sm" />
        </>
      )}

      {/* Florals */}
      {decorations.florals && (
        <>
          <span className="absolute top-2 left-2 text-2xl opacity-20 -rotate-45">🌿</span>
          <span className="absolute bottom-2 right-2 text-2xl opacity-20 rotate-45">🌸</span>
          <span className="absolute top-2 right-2 text-lg opacity-15 rotate-12">🌺</span>
          <span className="absolute bottom-2 left-2 text-lg opacity-15 -rotate-12">🍃</span>
        </>
      )}

      {/* Glow */}
      {decorations.glow && (
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at 50% 30%, rgba(251,191,146,0.15) 0%, transparent 60%), radial-gradient(ellipse at 50% 70%, rgba(244,164,196,0.1) 0%, transparent 60%)`,
          }}
        />
      )}

      {/* Confetti */}
      {decorations.confetti && (
        <>
          {['🎊', '🎉', '✨', '🎀', '💫', '⭐'].map((emoji, i) => (
            <span
              key={i}
              className="absolute text-base opacity-25"
              style={{
                top: `${10 + (i * 15) % 80}%`,
                left: `${5 + (i * 18) % 90}%`,
                transform: `rotate(${i * 30 - 60}deg)`,
              }}
            >
              {emoji}
            </span>
          ))}
        </>
      )}

      {/* Flourishes */}
      {decorations.flourishes && (
        <>
          <svg className="absolute top-4 left-1/2 -translate-x-1/2 w-32 h-4 opacity-15" viewBox="0 0 120 12">
            <path d="M0 6 Q30 0, 60 6 T120 6" fill="none" stroke={fontColor} strokeWidth="1" />
          </svg>
          <svg className="absolute bottom-6 left-1/2 -translate-x-1/2 w-32 h-4 opacity-15 rotate-180" viewBox="0 0 120 12">
            <path d="M0 6 Q30 0, 60 6 T120 6" fill="none" stroke={fontColor} strokeWidth="1" />
          </svg>
        </>
      )}
    </div>
  );
}
